/**
 * Dynamic Collection Feed - Client-Side Controller
 * Fetches precomputed Worker feed, handles personalization, shuffle, and smooth rendering
 */

(function () {
  'use strict';

  const DEFAULT_FEED_URL = 'https://collection-feed-engine.uncut-site.workers.dev/feed';
  const AI_BANNER_URL = 'https://heading-generator.uncut-site.workers.dev';
  let aiBanners = {};

  async function loadAIBanners() {
    try {
      const cacheBustUrl = `${AI_BANNER_URL}?t=${Math.floor(Date.now() / 60000)}`;
      const res = await fetch(cacheBustUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      aiBanners = await res.json();
    } catch (e) {
      console.warn('[Collection Feed] Could not load AI banners:', e);
      aiBanners = {};
    }
  }

  function toTitleCase(str) {
    return str.replace(/\b[a-z]/g, (c) => c.toUpperCase());
  }

  function cleanFirstHeading(title) {
    if (!title) return '';
    let clean = title
      .replace(/^\[.*?\]\s*/g, '')          // Removes [BEST], [NEW], [HOT], etc.
      .replace(/^[WM]\s*[\/|\-]\s*/i, '')   // Removes "W / ", "M / ", "W - ", etc.
      .replace(/\s*-\s*KR\b/gi, '')         // Removes " - KR", "- KR"
      .replace(/\bcopy(\s*\d+)?\b/gi, '')   // Removes "copy"
      .replace(/\bedits?\b/gi, '')          // Removes "edit" / "edits" (e.g. "Urban Night Edit")
      .replace(/\bACC\b/g, 'Accessories')   // "Hair ACC" -> "Hair Accessories"
      .replace(/\bacc\b/gi, 'Accessories')
      .replace(/Shop\s*\/\s*New Arrivals\.?/i, 'New Arrivals')
      .replace(/\s*[\.\/]+$/, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    if (clean === clean.toLowerCase() || /^[a-z]/.test(clean)) {
      clean = toTitleCase(clean);
    }

    // Fix specific words and cases
    clean = clean
      .replace(/\bwomen\b/gi, 'Women')
      .replace(/\bhoodies\b/gi, 'Hoodies')
      .replace(/\bsleeveless\b/gi, 'Sleeveless')
      .replace(/\bcrossbody\b/gi, 'Crossbody')
      .replace(/\bmidi\b/gi, 'Midi')
      .replace(/\bmini\b/gi, 'Mini')
      .replace(/\bhalter\b/gi, 'Halter')
      .replace(/\bstrap\b/gi, 'Strap')
      .replace(/\bcoquette\b/gi, 'Coquette')
      .replace(/\bblokette\b/gi, 'Blokette')
      .replace(/\bmen\b/gi, 'Men')
      .replace(/\btotes\b/gi, 'Totes')
      .replace(/\bmules\b/gi, 'Mules')
      .replace(/\bjelly\b/gi, 'Jelly')
      .replace(/\bcanvas\b/gi, 'Canvas')
      .replace(/\boff shoulder\b/gi, 'Off Shoulder')
      .replace(/\s{2,}/g, ' ')
      .trim();

    return clean;
  }

  function cleanString(str) {
    if (!str) return '';
    return str
      .replace(/\b29\s*cm\b/gi, '')
      .replace(/29CM/gi, '')
      .replace(/["']/g, '')
      .replace(/\bedits?\b/gi, '')
      .replace(/\b(elevat(e|ing|ion)|testament|tapestry|delv(e|ing)|curat(ed|ing)|unveil(ing)?|embrac(e|ing)|effortless(ly)?|eleganc(e)?|timeless|sophisticat(ed|ion)|indulg(e|ing)|realm|symphony|seamless(ly)?)\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function cleanSubheading(text) {
    if (!text) return '';
    let clean = cleanString(text);
    // If it uses the Korean vertical bar format (│ or |)
    if (clean.includes('│') || clean.includes('|')) {
      const parts = clean.split(/[│|]/);
      const left = parts[0].trim();
      let right = parts.slice(1).join(' ').trim().replace(/[.,;!?]+$/, '');
      const dangling = /\b(for|with|in|to|of|at|on|by|from|and|a|the|our)\s*$/i;
      while (dangling.test(right)) {
        right = right.replace(dangling, '').trim();
      }
      let words = right.split(/\s+/).filter(Boolean);
      if (words.length > 4) {
        words = words.slice(0, 4);
        right = words.join(' ');
      }
      while (dangling.test(right)) {
        right = right.replace(dangling, '').trim();
      }
      return `${left} │ ${right}`;
    }
    const sentences = clean.split(/(?<=[.!?])\s+/);
    let firstSentence = (sentences[0] || clean).replace(/[.!?]+$/, '').trim();
    const words = firstSentence.split(/\s+/).filter(Boolean);
    if (words.length > 7) {
      firstSentence = words.slice(0, 6).join(' ');
    }
    return firstSentence;
  }


  function findAICopy(collection) {
    if (!aiBanners || typeof aiBanners !== 'object' || Object.keys(aiBanners).length === 0) {
      return null;
    }

    const titleKey = (collection.title || '')
      .toLowerCase()
      .replace(/^\[.*?\]\s*/g, '')
      .replace(/^[wm]\s*[\/|\-]\s*/i, '')
      .replace(/\s*-\s*kr\b/gi, '')
      .replace(/[^a-z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // 1. Exact match on cleaned title (highest accuracy)
    if (titleKey && aiBanners[titleKey]) {
      return aiBanners[titleKey];
    }

    // 2. Token match ONLY on title (never match wrong garments)
    if (titleKey) {
      for (const [aiKey, aiVal] of Object.entries(aiBanners)) {
        if (titleKey === aiKey) return aiVal;
      }
      for (const [aiKey, aiVal] of Object.entries(aiBanners)) {
        const regex = new RegExp('\\b' + aiKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i');
        if (regex.test(titleKey)) {
          return aiVal;
        }
      }
    }

    // 3. Match on handle ONLY IF handle does NOT contain 'copy'
    // Handles with 'copy' (e.g. 'hoodies-copy' for 'Long Coat') are duplication artifacts that conflict with title
    const rawHandle = collection.handle || '';
    if (!/copy/i.test(rawHandle)) {
      const handleKey = rawHandle
        .toLowerCase()
        .replace(/[-_]/g, ' ')
        .replace(/^[wm]\s+/i, '')
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (handleKey && aiBanners[handleKey]) {
        return aiBanners[handleKey];
      }
    }

    return null;
  }

  async function initCollectionFeed() {
    const feedWrappers = document.querySelectorAll('.dcf-wrapper, .collection-feed-wrapper');
    if (!feedWrappers.length) return;

    // Load AI generated banners concurrently
    await loadAIBanners();

    feedWrappers.forEach((wrapper) => {
      renderFeedInstance(wrapper);
    });
  }

  async function renderFeedInstance(wrapper) {
    const endpoint = (wrapper.dataset.endpoint && wrapper.dataset.endpoint.trim())
      ? wrapper.dataset.endpoint.trim()
      : DEFAULT_FEED_URL;

    const maxCollections = parseInt(wrapper.dataset.maxCollections || '100', 10);
    const maxProductsPerRow = parseInt(wrapper.dataset.productsPerRow || '12', 10);
    const shouldShuffle = wrapper.dataset.shuffle === 'true';
    const customerSegment = wrapper.dataset.customerSegment || getCookie('customer_segment') || '';
    const mountPoint = wrapper.querySelector('.dcf-content') || wrapper.querySelector('.collection-feed-content');
    const skeleton = wrapper.querySelector('.dcf-skeleton') || wrapper.querySelector('.collection-feed-skeleton');
    const seoFallback = wrapper.querySelector('[data-seo-fallback]');

    const filterByMenu = wrapper.dataset.filterByMenu === 'true';
    const menuHandlesRaw = wrapper.dataset.menuHandles || '';
    const menuHandles = menuHandlesRaw
      ? menuHandlesRaw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
      : [];

    // Build URL with personalization query parameters and menu handles
    let fetchUrl = endpoint;
    const urlObj = new URL(endpoint, window.location.origin);
    if (customerSegment) {
      urlObj.searchParams.set('customer_type', customerSegment);
    }
    if (filterByMenu && menuHandles.length > 0) {
      urlObj.searchParams.set('handles', menuHandles.join(','));
    }
    fetchUrl = urlObj.toString();

    let feedData = null;
    try {
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`Feed fetch returned status ${response.status}`);
      }

      feedData = await response.json();
    } catch (err) {
      console.error('[Collection Feed] Failed to fetch feed:', err);
      hideSkeleton(wrapper);
      if (seoFallback) {
        seoFallback.style.display = 'block';
      }
      return;
    }

    let collections = feedData.collections || [];
    if (!collections.length) {
      hideSkeleton(wrapper);
      return;
    }

    // Client-side guard: if filtering by menu, ensure only menu handles are displayed
    if (filterByMenu && menuHandles.length > 0) {
      const handleSet = new Set(menuHandles);
      collections = collections.filter((c) => handleSet.has(c.handle?.toLowerCase()));
    }

    // Limit collections count
    collections = collections.slice(0, maxCollections);

    // Optional Fisher-Yates shuffle
    if (shouldShuffle) {
      for (let i = collections.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [collections[i], collections[j]] = [collections[j], collections[i]];
      }
    }

    // Build DOM tree
    const fragment = document.createDocumentFragment();
    const usedBannerHeadings = new Set();

    collections.forEach((collection) => {
      if (!collection.products || collection.products.length === 0) return;

      const row = document.createElement('div');
      row.className = 'dcf-collection-block';
      row.setAttribute('data-handle', collection.handle);

      const productsToRender = collection.products.slice(0, maxProductsPerRow);

      // Random banner image from the collection's product images
      let bannerImg = collection.bannerImage || collection.image;
      if (collection.productImages && collection.productImages.length > 0) {
        const randIdx = Math.floor(Math.random() * collection.productImages.length);
        bannerImg = collection.productImages[randIdx];
      }

      // Editorial AI Heading & Subheading lookup
      const firstHeading = cleanFirstHeading(collection.title);
      const ai = findAICopy(collection);

      let heading = ai?.heading ? cleanString(ai.heading) : '';

      // Ensure banner heading is nearest to the actual collection category and does not repeat firstHeading
      if (!heading || heading.toLowerCase() === firstHeading.toLowerCase()) {
        if (/long coat/i.test(firstHeading)) {
          heading = 'Classic Long Coat';
        } else if (/coat/i.test(firstHeading)) {
          heading = 'Tailored Coat';
        } else if (/blouse/i.test(firstHeading)) {
          heading = 'Women Blouse';
        } else if (/hoodie/i.test(firstHeading)) {
          heading = 'Cozy Hoodies';
        } else if (/sweatshirt/i.test(firstHeading)) {
          heading = 'Relaxed Sweatshirts';
        } else if (/cardigan/i.test(firstHeading)) {
          heading = 'Soft Cardigans';
        } else if (/windbreaker/i.test(firstHeading)) {
          heading = 'Light Windbreaker';
        } else if (/leather jacket/i.test(firstHeading)) {
          heading = 'Leather Jacket';
        } else if (/jacket/i.test(firstHeading)) {
          heading = 'Warm Outerwear';
        } else if (/knit/i.test(firstHeading)) {
          heading = 'Cozy Knitwear';
        } else if (/jean|denim/i.test(firstHeading)) {
          heading = 'Everyday Denim';
        } else if (/trouser|slack/i.test(firstHeading)) {
          heading = 'Tailored Trousers';
        } else if (/mini skirt/i.test(firstHeading)) {
          heading = 'Mini Skirts';
        } else if (/midi skirt/i.test(firstHeading)) {
          heading = 'Midi Skirts';
        } else if (/long skirt/i.test(firstHeading)) {
          heading = 'Long Skirts';
        } else if (/skirt/i.test(firstHeading)) {
          heading = 'Pleated Skirts';
        } else if (/mini dress/i.test(firstHeading)) {
          heading = 'Mini Dresses';
        } else if (/midi dress/i.test(firstHeading)) {
          heading = 'Midi Dresses';
        } else if (/maxi dress/i.test(firstHeading)) {
          heading = 'Maxi Dresses';
        } else if (/dress/i.test(firstHeading)) {
          heading = 'Dress Collection';
        } else if (/boot/i.test(firstHeading)) {
          heading = 'Leather Boots';
        } else if (/shoe|loafer/i.test(firstHeading)) {
          heading = 'Classic Footwear';
        } else if (/beanie|scarf/i.test(firstHeading)) {
          heading = 'Warm Accessories';
        } else if (/t-shirt|tee/i.test(firstHeading)) {
          heading = 'Essential Tees';
        } else {
          heading = firstHeading;
        }
      }

      // Deduplicate across the feed so no two banner headings are identical
      let finalHeading = heading;
      if (usedBannerHeadings.has(finalHeading.toLowerCase())) {
        const altOptions = [
          `The ${finalHeading}`,
          `Modern ${finalHeading}`,
          `Everyday ${finalHeading}`,
          `Classic ${finalHeading}`,
          `${finalHeading} Collection`
        ];
        for (const alt of altOptions) {
          if (!usedBannerHeadings.has(alt.toLowerCase())) {
            finalHeading = alt;
            break;
          }
        }
      }
      usedBannerHeadings.add(finalHeading.toLowerCase());
      heading = finalHeading;

      const discountPct = collection.maxDiscountPercent > 0 ? collection.maxDiscountPercent : 20;
      const subHeading = `${firstHeading} │ Get up to ${discountPct}% off`;

      // Max discount badge in English
      const discountBadge = collection.maxDiscountPercent > 0
        ? `<div class="dcf-banner-badge-wrap"><div class="dcf-banner-badge">Up to ${collection.maxDiscountPercent}%</div></div>`
        : '';

      row.innerHTML = `
        <div class="dcf-header">
          <h2 class="dcf-heading">${escapeHtml(firstHeading)}</h2>
          <a class="dcf-view-all" href="/collections/${escapeHtml(collection.handle)}" role="button">
            <span>View all</span>
            <div class="dcf-view-all-arrow">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" fill-rule="evenodd" d="m17.131 12-8.565 8.566-1.132-1.132L14.87 12 7.434 4.566l1.132-1.132z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </a>
        </div>

        <div class="dcf-banner-wrap">
          <a href="/collections/${escapeHtml(collection.handle)}" class="dcf-banner">
            ${bannerImg ? `<img src="${escapeHtml(bannerImg)}" alt="${escapeHtml(heading)}" loading="lazy" class="dcf-banner-img" width="716" height="476">` : ''}
            ${discountBadge}
            <div class="dcf-banner-text-overlay">
              <h3 class="dcf-banner-title">${escapeHtml(heading)}</h3>
              <p class="dcf-banner-sub">${escapeHtml(subHeading)}</p>
            </div>
          </a>
        </div>

        <div class="dcf-slider-wrap">
          <div class="dcf-slider">
            ${productsToRender.map((product) => renderProductCard(product)).join('')}
          </div>
        </div>
      `;

      fragment.appendChild(row);
    });

    if (mountPoint) {
      mountPoint.innerHTML = '';
      mountPoint.appendChild(fragment);
    }

    // Hide skeleton and swap out SEO fallback seamlessly
    hideSkeleton(wrapper);
    if (seoFallback) {
      seoFallback.style.display = 'none';
    }
  }

  function renderProductCard(product) {
    const formattedPrice = formatMoney(product.price, product.currency);
    const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
    const formattedCompareAt = hasDiscount ? formatMoney(product.compareAtPrice, product.currency) : '';
    const discountPercent = hasDiscount
      ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
      : 0;

    return `
      <div class="dcf-card">
        <a href="/products/${escapeHtml(product.handle)}" class="dcf-card-img-wrap">
          ${product.image
            ? `<img
                 loading="lazy"
                 decoding="async"
                 src="${escapeHtml(product.image)}"
                 alt="${escapeHtml(product.title)}"
                 class="dcf-card-img"
                 width="400"
                 height="400"
               >`
            : `<div class="dcf-media-placeholder"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>`
          }
          <button class="dcf-card-wishlist-btn" data-product-handle="${escapeHtml(product.handle)}" aria-label="Add to wishlist" type="button">
            <svg class="dcf-card-heart-icon" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3 c1.74 0 3.41 0.81 4.5 2.09 C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5 c0 3.78-3.4 6.86-8.55 11.54 L12 21.35z"/>
            </svg>
          </button>
        </a>

        <div class="dcf-card-title-wrap">
          <h4 class="dcf-card-title">
            <a href="/products/${escapeHtml(product.handle)}">${escapeHtml(product.title)}</a>
          </h4>
        </div>

        <div class="dcf-card-price-wrap">
          <p class="dcf-card-price">
            ${hasDiscount
              ? `<s class="dcf-card-compare-at">${formattedCompareAt}</s>
                 <span class="dcf-card-discount">
                   ${discountPercent}%
                   <span class="dcf-card-sale-price">${formattedPrice}</span>
                 </span>`
              : `<span class="dcf-card-regular-price">${formattedPrice}</span>`
            }
          </p>
        </div>

        <div class="dcf-card-tags">
          <span class="dcf-card-tag">Coupon</span>
          <span class="dcf-card-tag">Free ship</span>
        </div>
      </div>
    `;
  }

  function hideSkeleton(wrapper) {
    const skeleton = wrapper.querySelector('.dcf-skeleton') || wrapper.querySelector('.collection-feed-skeleton');
    if (skeleton) {
      skeleton.classList.add('is-hidden');
    }
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, function (m) {
      switch (m) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return m;
      }
    });
  }

  function formatMoney(amount, currency = 'USD') {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (e) {
      return `${currency} ${Number(amount).toFixed(2)}`;
    }
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return match ? decodeURIComponent(match[3]) : null;
  }

  // Auto-init on DOMContentLoaded or immediate execution
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionFeed);
  } else {
    initCollectionFeed();
  }

  // Listen for Shopify theme customizer events
  document.addEventListener('shopify:section:load', async function (e) {
    const wrapper = e.target.querySelector('.dcf-wrapper, .collection-feed-wrapper');
    if (wrapper) {
      if (Object.keys(aiBanners).length === 0) {
        await loadAIBanners();
      }
      renderFeedInstance(wrapper);
    }
  });
})();

